import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { GripVertical, Mail, Trash2, Check } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import SuburbInput from "../components/SuburbInput";

const DISCIPLINES = ["Residential Architecture", "Residential Interiors", "Commercial Architecture", "Commercial Fit-outs"];
const EMPTY_ROW = () => ({
  _key: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `row-${Math.random().toString(36).slice(2, 10)}`,
  title: "", summary: "", body: "", discipline: DISCIPLINES[0],
  year: "", location: "", published: false, featured: false, featured_in: "",
});

export default function Admin() {
  const { user, logout } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [mode, setMode] = useState("list"); // list | single | bulk
  const [singleForm, setSingleForm] = useState(EMPTY_ROW());
  const [bulkRows, setBulkRows] = useState([EMPTY_ROW(), EMPTY_ROW(), EMPTY_ROW()]);
  const dragIndex = useRef(null);   // index of row currently being dragged
  const [dropTarget, setDropTarget] = useState(null);   // index of row to drop above

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ["admin", "projects"],
    queryFn: async () => (await api.get("/admin/projects")).data,
  });

  const createOne = useMutation({
    mutationFn: async (payload) => {
      const featured_in_arr = (payload.featured_in || "")
        .split(",").map(s => s.trim()).filter(Boolean);
      const { featured_in, ...rest } = payload;
      const { data } = await api.post("/admin/projects", rest);
      if (featured_in_arr.length > 0) {
        const { data: updated } = await api.patch(`/admin/projects/${data.id}`, { featured_in: featured_in_arr });
        return updated;
      }
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["admin", "projects"] });
      toast.success("Project created — now add images.");
      navigate(`/admin/projects/${data.id}`);
    },
    onError: () => toast.error("Failed to create project."),
  });

  const createBulk = useMutation({
    mutationFn: async ({ payloads, feats }) => {
      const { data } = await api.post("/admin/projects/bulk", payloads);
      const created = data.created || [];
      // Patch featured_in per created project (bulk endpoint doesn't accept it)
      for (let i = 0; i < created.length; i++) {
        const list = (feats && feats[i]) || [];
        if (list.length > 0) {
          try {
            await api.patch(`/admin/projects/${created[i].id}`, { featured_in: list });
          } catch (err) {
            console.error(`Failed to set featured_in for project ${created[i].id}:`, err);
          }
        }
      }
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["admin", "projects"] });
      toast.success(`${data.count} projects created.`);
      setMode("list");
      setBulkRows([EMPTY_ROW(), EMPTY_ROW(), EMPTY_ROW()]);
    },
    onError: () => toast.error("Bulk create failed."),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/projects/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "projects"] }); toast.success("Deleted."); },
  });

  const togglePublish = useMutation({
    mutationFn: async ({ id, published }) => (await api.patch(`/admin/projects/${id}`, { published })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "projects"] }),
  });

  const toggleFeatured = useMutation({
    mutationFn: async ({ id, featured }) => (await api.patch(`/admin/projects/${id}`, { featured })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "projects"] }),
  });

  // ---------- Contact enquiries ----------
  const [showEnquiries, setShowEnquiries] = useState(false);
  const { data: enquiryData = { messages: [], unread: 0, total: 0 }, isError: enquiryError } = useQuery({
    queryKey: ["admin", "contact-messages"],
    queryFn: async () => (await api.get("/admin/contact-messages")).data,
    refetchInterval: 60_000, // poll every minute so the badge stays live-ish
  });
  const messages = enquiryData.messages || [];
  const unreadCount = enquiryData.unread || 0;

  const markRead = useMutation({
    mutationFn: async ({ id, read }) => (await api.patch(`/admin/contact-messages/${id}`, { read })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "contact-messages"] }),
    onError: () => toast.error("Could not update enquiry."),
  });
  const markAllRead = useMutation({
    mutationFn: async () => (await api.post("/admin/contact-messages/mark-all-read")).data,
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["admin", "contact-messages"] });
      toast.success(res.marked === 0 ? "No unread enquiries." : `Marked ${res.marked} as read.`);
    },
  });
  const deleteMsg = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/contact-messages/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "contact-messages"] });
      toast.success("Enquiry deleted.");
    },
    onError: () => toast.error("Could not delete enquiry."),
  });

  // Renumber every project's sort_order to its current display index (0..N-1).
  // Cleans up sparse or duplicate values left after big reshuffles.
  const renumberAll = useMutation({
    mutationFn: async () => {
      const patches = [];
      projects.forEach((p, idx) => {
        if ((p.sort_order ?? 999) !== idx) patches.push({ id: p.id, sort_order: idx });
      });
      if (patches.length === 0) return { changed: 0 };
      // Optimistic cache update so the numbers snap to clean 0..N-1.
      qc.setQueryData(["admin", "projects"], projects.map((p, idx) => ({ ...p, sort_order: idx })));
      await Promise.all(patches.map((pt) => api.patch(`/admin/projects/${pt.id}`, { sort_order: pt.sort_order })));
      return { changed: patches.length };
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["admin", "projects"] });
      toast.success(res.changed === 0
        ? "Order already clean — nothing to renumber."
        : `Renumbered ${res.changed} project${res.changed === 1 ? "" : "s"} to 0…${projects.length - 1}.`);
    },
    onError: () => toast.error("Renumber failed — please try again."),
  });

  // Place a project at a specific 1-based position — the typed-number flow.
  // Removes the project from its current spot in the ordered list, inserts it
  // at the target index, then PATCHes every project whose new sort_order
  // differs from its current one. Result: sequence is always 0..N-1 clean.
  const placeAt = useMutation({
    mutationFn: async ({ id, position }) => {
      const list = [...projects]; // already sorted by backend (sort_order asc)
      const from = list.findIndex((p) => p.id === id);
      if (from === -1) return true;
      const to = Math.max(0, Math.min(list.length - 1, position));
      if (from === to) return true;
      const [moved] = list.splice(from, 1);
      list.splice(to, 0, moved);
      // Compute the diff — only patch what actually changed.
      const patches = [];
      list.forEach((p, idx) => {
        if ((p.sort_order ?? 999) !== idx) patches.push({ id: p.id, sort_order: idx });
      });
      // Optimistic cache update so the UI reflows immediately.
      qc.setQueryData(["admin", "projects"], list.map((p, idx) => ({ ...p, sort_order: idx })));
      await Promise.all(patches.map((pt) => api.patch(`/admin/projects/${pt.id}`, { sort_order: pt.sort_order })));
      return true;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "projects"] }),
    onError: () => {
      toast.error("Could not update priority.");
      qc.invalidateQueries({ queryKey: ["admin", "projects"] });
    },
  });

  // Swap two projects' sort_order — used by the ↑/↓ buttons on the admin list
  // so studio can rearrange already-published projects (not just fresh bulk).
  const swapOrder = useMutation({
    mutationFn: async ({ a, b }) => {
      // Assign explicit sort_order values. Anchor from the smaller of the two
      // existing orders so we never collide with other projects.
      const aOrder = typeof a.sort_order === "number" ? a.sort_order : 999;
      const bOrder = typeof b.sort_order === "number" ? b.sort_order : 999;
      await api.patch(`/admin/projects/${a.id}`, { sort_order: bOrder });
      await api.patch(`/admin/projects/${b.id}`, { sort_order: aOrder });
      return true;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "projects"] }),
    onError: () => toast.error("Could not reorder — please try again."),
  });
  const moveProject = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= projects.length) return;
    // If the two rows currently share the same sort_order (e.g. both 999)
    // give the one going UP a value one below the other. Otherwise just
    // swap the two values.
    const a = projects[i];
    const b = projects[j];
    if ((a.sort_order ?? 999) === (b.sort_order ?? 999)) {
      // Rebase around the pair: upper gets (base - 1), lower keeps base.
      const base = a.sort_order ?? 999;
      const rebased = dir < 0
        ? { a: { ...a, sort_order: base - 1 }, b: { ...b, sort_order: base } }
        : { a: { ...a, sort_order: base }, b: { ...b, sort_order: base - 1 } };
      swapOrder.mutate({ a: rebased.a, b: rebased.b });
      return;
    }
    swapOrder.mutate({ a, b });
  };

  const updateBulkRow = (i, key, val) => setBulkRows((rs) => rs.map((r, idx) => idx === i ? { ...r, [key]: val } : r));

  // Drag-reorder handlers — array index at submit becomes the project's sort_order.
  const onRowDragStart = (i) => (e) => {
    dragIndex.current = i;
    e.dataTransfer.effectAllowed = "move";
    // Firefox requires setData for a drag to fire; some browsers can throw
    // on this call in obscure edge cases. It is a best-effort hint, not a
    // functional requirement — safe to swallow silently.
    try { e.dataTransfer.setData("text/plain", String(i)); } catch { /* ignored — Firefox quirk, non-fatal */ }
  };
  const onRowDragOver = (i) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dropTarget !== i) setDropTarget(i);
  };
  const onRowDragLeave = (i) => () => {
    if (dropTarget === i) setDropTarget(null);
  };
  const onRowDrop = (i) => (e) => {
    e.preventDefault();
    const from = dragIndex.current;
    dragIndex.current = null;
    setDropTarget(null);
    if (from == null || from === i) return;
    setBulkRows((rs) => {
      const next = rs.slice();
      const [moved] = next.splice(from, 1);
      next.splice(i, 0, moved);
      return next;
    });
  };
  const onRowDragEnd = () => {
    dragIndex.current = null;
    setDropTarget(null);
  };
  const removeBulkRow = (i) => setBulkRows((rs) => rs.length > 1 ? rs.filter((_, idx) => idx !== i) : rs);
  const moveBulkRow = (i, dir) => setBulkRows((rs) => {
    const j = i + dir;
    if (j < 0 || j >= rs.length) return rs;
    const next = rs.slice();
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  const submitBulk = () => {
    const valid = bulkRows.filter((r) => r.title.trim());
    if (valid.length === 0) { toast.error("Add at least one project with a title."); return; }
    // Featured-in list per row (preserve order to align with sort_order on the backend)
    const feats = valid.map((r) => (r.featured_in || "").split(",").map(s => s.trim()).filter(Boolean));
    const payloads = valid.map(({ featured_in, _key, ...rest }, idx) => ({ ...rest, sort_order: idx }));
    createBulk.mutate({ payloads, feats });
  };

  return (
    <div data-testid="admin-page" className="pt-28 md:pt-36 container-w pb-32">
      <div className="flex justify-between items-start mb-16 flex-wrap gap-4">
        <div>
          <div className="mono-label mb-2">Studio · Admin</div>
          <h1 className="font-display italic text-4xl md:text-6xl tracking-tight">Projects.</h1>
        </div>
        <div className="flex items-center gap-6">
          {/* Enquiries badge — click to toggle the messages panel */}
          <button
            type="button"
            onClick={() => setShowEnquiries((s) => !s)}
            className="relative mono-label link-underline flex items-center gap-2"
            title="Recent contact enquiries"
            data-testid="admin-enquiries-toggle"
          >
            <Mail size={14} strokeWidth={1.5} />
            <span>Enquiries</span>
            {unreadCount > 0 && (
              <span
                className="absolute -top-2 -right-4 bg-marine text-paper text-[10px] leading-none px-1.5 py-0.5 min-w-[18px] text-center tabular-nums"
                style={{ borderRadius: "999px" }}
                data-testid="admin-enquiries-badge"
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>
          <span className="mono-label text-ash">{user?.email}</span>
          <button onClick={logout} className="mono-label link-underline" data-testid="admin-logout">Sign out →</button>
        </div>
      </div>

      {/* ---------- Enquiries panel ---------- */}
      {showEnquiries && (
        <div className="border border-ink/15 mb-12 bg-bone/30" data-testid="admin-enquiries-panel">
          <div className="flex items-baseline justify-between border-b border-ink/15 px-6 py-4 flex-wrap gap-3">
            <div className="mono-label">
              Enquiries — <span className="text-marine">{enquiryData.total} total</span>
              {unreadCount > 0 && <span className="text-marine"> · {unreadCount} unread</span>}
            </div>
            <div className="flex items-center gap-4">
              {unreadCount > 0 && (
                <button
                  onClick={() => markAllRead.mutate()}
                  disabled={markAllRead.isPending}
                  className="mono-label link-underline text-marine"
                  data-testid="admin-mark-all-read"
                >
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setShowEnquiries(false)}
                className="mono-label text-ash hover:text-ink"
                title="Close panel"
              >
                Close ×
              </button>
            </div>
          </div>
          {enquiryError ? (
            <div className="px-6 py-10 text-center" data-testid="admin-enquiries-error">
              <div className="mono-label text-red-700 mb-2">Could not load enquiries.</div>
              <div className="text-ash text-sm">Check your connection or reload. The badge count may be out of date until the connection recovers.</div>
            </div>
          ) : messages.length === 0 ? (
            <div className="px-6 py-16 text-center text-ash">No enquiries yet.</div>
          ) : (
            <ul className="divide-y divide-ink/10 max-h-[560px] overflow-y-auto">
              {messages.map((m) => {
                const details = [m.suburb, m.project_type, m.house_size, m.service].filter(Boolean).join(" · ");
                const when = m.created_at ? new Date(m.created_at).toLocaleString("en-AU", {
                  day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
                }) : "";
                return (
                  <li
                    key={m.id}
                    className={`px-6 py-5 ${m.read ? "" : "bg-paper"}`}
                    data-testid={`admin-enquiry-${m.id}`}
                  >
                    <div className="flex items-baseline justify-between flex-wrap gap-3 mb-2">
                      <div className="flex items-center gap-3 flex-wrap">
                        {!m.read && (
                          <span
                            className="w-2 h-2 bg-marine inline-block"
                            style={{ borderRadius: "999px" }}
                            aria-label="Unread"
                            data-testid={`admin-enquiry-unread-dot-${m.id}`}
                          />
                        )}
                        <span className="font-display italic text-lg">{m.name}</span>
                        <a
                          href={`mailto:${m.email}?subject=Re: your enquiry to TWPCo`}
                          className="mono-label link-underline text-marine"
                          data-testid={`admin-enquiry-mailto-${m.id}`}
                        >
                          {m.email}
                        </a>
                        {m.phone && (
                          <a
                            href={`tel:${m.phone.replace(/[^0-9+]/g, "")}`}
                            className="mono-label link-underline text-marine"
                            data-testid={`admin-enquiry-phone-${m.id}`}
                          >
                            {m.phone}
                          </a>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="mono-label text-ash">{when}</span>
                        {!m.read ? (
                          <button
                            onClick={() => markRead.mutate({ id: m.id, read: true })}
                            className="mono-label text-marine hover:underline flex items-center gap-1"
                            title="Mark as read"
                            data-testid={`admin-enquiry-mark-read-${m.id}`}
                          >
                            <Check size={12} strokeWidth={1.8} /> Read
                          </button>
                        ) : (
                          <button
                            onClick={() => markRead.mutate({ id: m.id, read: false })}
                            className="mono-label text-ash hover:text-marine"
                            title="Mark unread"
                            data-testid={`admin-enquiry-mark-unread-${m.id}`}
                          >
                            Unread
                          </button>
                        )}
                        <button
                          onClick={() => {
                            if (window.confirm(`Delete enquiry from ${m.name}? This cannot be undone.`)) {
                              deleteMsg.mutate(m.id);
                            }
                          }}
                          className="mono-label text-ash hover:text-red-600 flex items-center gap-1"
                          title="Delete enquiry"
                          data-testid={`admin-enquiry-delete-${m.id}`}
                        >
                          <Trash2 size={12} strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                    {details && <div className="mono-label text-ash mb-2">{details}</div>}
                    <p className="text-sm leading-relaxed whitespace-pre-wrap text-ink/90 max-w-3xl">{m.message}</p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* Mode selector */}
      <div className="border-y border-ink/15 py-6 mb-12 flex flex-wrap items-center justify-between gap-4">
        <div className="mono-label">{projects.length} projects</div>
        <div className="flex items-center gap-4 flex-wrap">
          <button
            onClick={() => { setMode(mode === "single" ? "list" : "single"); setSingleForm(EMPTY_ROW()); }}
            className={`mono-label px-6 py-3 border transition-colors duration-500 ${mode === "single" ? "bg-marine text-paper border-marine" : "border-ink/20 text-marine hover:bg-marine hover:text-paper"}`}
            data-testid="new-single-btn"
          >
            {mode === "single" ? "Cancel" : "+ New project"}
          </button>
          <button
            onClick={() => setMode(mode === "bulk" ? "list" : "bulk")}
            className={`mono-label px-6 py-3 border transition-colors duration-500 ${mode === "bulk" ? "bg-marine text-paper border-marine" : "border-ink/20 text-marine hover:bg-marine hover:text-paper"}`}
            data-testid="new-bulk-btn"
          >
            {mode === "bulk" ? "Cancel" : "+ Bulk add"}
          </button>
        </div>
      </div>

      {/* ---------- Single-project quick form (5 core fields) ---------- */}
      {mode === "single" && (
        <form
          onSubmit={(e) => { e.preventDefault(); if (!singleForm.title) return; createOne.mutate(singleForm); }}
          className="border-b border-ink/15 pb-16 mb-16 grid grid-cols-1 md:grid-cols-12 gap-6"
          data-testid="new-single-form"
        >
          <div className="md:col-span-12 mono-label text-ash">Step 1 — Enter the essentials. You&rsquo;ll add hero image, gallery and crop next.</div>

          <label className="md:col-span-6 block">
            <span className="mono-label block mb-2">a) Project name</span>
            <input
              type="text" required autoFocus
              value={singleForm.title}
              onChange={(e) => setSingleForm({ ...singleForm, title: e.target.value })}
              className="w-full bg-transparent border-b border-ink/20 py-2 text-2xl md:text-3xl font-display italic focus:outline-none focus:border-marine"
              data-testid="single-title"
              placeholder="e.g. Coogee House"
            />
          </label>
          <label className="md:col-span-3 block">
            <span className="mono-label block mb-2">Discipline</span>
            <select
              value={singleForm.discipline}
              onChange={(e) => setSingleForm({ ...singleForm, discipline: e.target.value })}
              className="w-full bg-transparent border-b border-ink/20 py-2 mono-label focus:outline-none"
              data-testid="single-discipline"
            >
              {DISCIPLINES.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          <label className="md:col-span-3 block">
            <span className="mono-label block mb-2">Year</span>
            <input
              type="text" value={singleForm.year}
              onChange={(e) => setSingleForm({ ...singleForm, year: e.target.value })}
              className="w-full bg-transparent border-b border-ink/20 py-2 mono-label focus:outline-none"
              placeholder="2024"
              data-testid="single-year"
            />
          </label>
          <label className="md:col-span-6 block">
            <span className="mono-label block mb-2">Location <span className="text-ash">— starts searching Australian suburbs after 2 chars</span></span>
            <SuburbInput
              value={singleForm.location}
              onChange={(v) => setSingleForm((s) => ({ ...s, location: v }))}
              onCommit={(v) => setSingleForm((s) => ({ ...s, location: v }))}
              className="w-full bg-transparent border-b border-ink/20 py-2 mono-label focus:outline-none focus:border-marine"
              placeholder="e.g. Coogee NSW"
              data-testid="single-location"
            />
          </label>

          <label className="md:col-span-12 block">
            <span className="mono-label block mb-2">b) Short description <span className="text-ash">— shown in listings &amp; cards</span></span>
            <textarea
              rows={2}
              value={singleForm.summary}
              onChange={(e) => setSingleForm({ ...singleForm, summary: e.target.value })}
              className="w-full bg-transparent border-b border-ink/20 py-2 text-lg focus:outline-none resize-none"
              placeholder="One-line essence. Used on cards, index, and project header."
              data-testid="single-summary"
            />
          </label>

          <label className="md:col-span-12 block">
            <span className="mono-label block mb-2">c) Big description <span className="text-ash">— full write-up on the detailed project page</span></span>
            <textarea
              rows={6}
              value={singleForm.body}
              onChange={(e) => setSingleForm({ ...singleForm, body: e.target.value })}
              className="w-full bg-transparent border-b border-ink/20 py-2 font-display italic text-xl leading-relaxed focus:outline-none resize-none"
              placeholder="Tell the story of the project. Site, brief, materials, moments."
              data-testid="single-body"
            />
          </label>

          <label className="md:col-span-12 block">
            <span className="mono-label block mb-2">Featured in <span className="text-ash">— optional publications / awards (comma-separated)</span></span>
            <input
              type="text"
              value={singleForm.featured_in}
              onChange={(e) => setSingleForm({ ...singleForm, featured_in: e.target.value })}
              className="w-full bg-transparent border-b border-ink/20 py-2 focus:outline-none"
              placeholder="e.g. Vogue Living, Est Living, Habitus"
              data-testid="single-featured-in"
            />
          </label>

          <div className="md:col-span-6 mono-label text-ash">d) Hero picture &nbsp; e) Gallery — <span className="text-ink">continue on next screen</span></div>
          <button
            type="submit"
            disabled={createOne.isPending}
            className="md:col-span-3 md:col-start-10 mono-label px-6 py-3 bg-marine text-paper hover:opacity-90 transition-opacity disabled:opacity-40"
            data-testid="single-submit"
          >
            {createOne.isPending ? "Creating…" : "Create & add images →"}
          </button>
        </form>
      )}

      {/* ---------- Bulk multi-project form — parity with single-project form ---------- */}
      {mode === "bulk" && (
        <div className="border-b border-ink/15 pb-16 mb-16" data-testid="bulk-form">
          <div className="mono-label text-ash mb-6">
            Enter multiple projects at once. <span className="text-ink">Drag the grip to reorder</span> — the row order is the order they will appear on the Work page. Attach images per project after creating.
          </div>
          <div className="space-y-10">
            {bulkRows.map((r, i) => {
              const isDropping = dropTarget === i;
              return (
              <div
                key={r._key}
                onDragOver={onRowDragOver(i)}
                onDragLeave={onRowDragLeave(i)}
                onDrop={onRowDrop(i)}
                onDragEnd={onRowDragEnd}
                className={`border-t pt-6 grid grid-cols-1 md:grid-cols-12 gap-4 transition-colors duration-200 ${isDropping ? "border-marine bg-bone/60" : "border-ink/10"}`}
                data-testid={`bulk-row-${i}`}
              >
                <div className="md:col-span-1 flex items-start gap-2 pt-2">
                  <button
                    type="button"
                    draggable
                    onDragStart={onRowDragStart(i)}
                    onDragEnd={onRowDragEnd}
                    className="cursor-grab active:cursor-grabbing text-ash hover:text-marine transition-colors p-1 -ml-1"
                    aria-label={`Drag row ${i + 1} to reorder`}
                    title="Drag to reorder — row order = order on Work"
                    data-testid={`bulk-drag-${i}`}
                    data-cursor="hover"
                  >
                    <GripVertical size={20} strokeWidth={1.2} aria-hidden />
                  </button>
                  <span className="mono-label pt-1.5">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <input
                  className="md:col-span-5 bg-transparent border-b border-ink/20 py-2 font-display text-2xl focus:outline-none focus:border-marine"
                  placeholder="a) Project name"
                  value={r.title}
                  onChange={(e) => updateBulkRow(i, "title", e.target.value)}
                  data-testid={`bulk-title-${i}`}
                />
                <select
                  className="md:col-span-3 bg-transparent border-b border-ink/20 py-2 mono-label focus:outline-none"
                  value={r.discipline}
                  onChange={(e) => updateBulkRow(i, "discipline", e.target.value)}
                  data-testid={`bulk-discipline-${i}`}
                >
                  {DISCIPLINES.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                <input
                  className="md:col-span-3 bg-transparent border-b border-ink/20 py-2 mono-label focus:outline-none"
                  placeholder="Year"
                  value={r.year}
                  onChange={(e) => updateBulkRow(i, "year", e.target.value)}
                  data-testid={`bulk-year-${i}`}
                />

                {/* Location w/ AU suburb autocomplete */}
                <div className="md:col-span-6 md:col-start-2">
                  <span className="mono-label text-ash block mb-1">Location</span>
                  <SuburbInput
                    value={r.location}
                    onChange={(v) => updateBulkRow(i, "location", v)}
                    onCommit={(v) => updateBulkRow(i, "location", v)}
                    className="w-full bg-transparent border-b border-ink/20 py-2 mono-label focus:outline-none focus:border-marine"
                    placeholder="Start typing an Australian suburb…"
                    data-testid={`bulk-location-${i}`}
                  />
                </div>
                <input
                  className="md:col-span-5 bg-transparent border-b border-ink/20 py-2 mono-label focus:outline-none self-end"
                  placeholder="Featured in (optional, comma-separated)"
                  value={r.featured_in}
                  onChange={(e) => updateBulkRow(i, "featured_in", e.target.value)}
                  data-testid={`bulk-featured-in-${i}`}
                />

                <textarea
                  rows={2}
                  className="md:col-span-11 md:col-start-2 bg-transparent border-b border-ink/20 py-2 focus:outline-none resize-none"
                  placeholder="b) Short description — used on cards"
                  value={r.summary}
                  onChange={(e) => updateBulkRow(i, "summary", e.target.value)}
                  data-testid={`bulk-summary-${i}`}
                />
                <textarea
                  rows={4}
                  className="md:col-span-11 md:col-start-2 bg-transparent border-b border-ink/20 py-2 font-display text-lg leading-relaxed focus:outline-none resize-none"
                  placeholder="c) Big description — full write-up shown on the detailed project page"
                  value={r.body}
                  onChange={(e) => updateBulkRow(i, "body", e.target.value)}
                  data-testid={`bulk-body-${i}`}
                />
                {/* Row controls: keyboard-accessible ↑↓ + remove */}
                <div className="md:col-span-11 md:col-start-2 flex gap-4 items-center pt-1">
                  <button type="button" onClick={() => moveBulkRow(i, -1)} disabled={i === 0} className="mono-label link-underline disabled:opacity-30 disabled:no-underline" data-testid={`bulk-up-${i}`}>↑ Move up</button>
                  <button type="button" onClick={() => moveBulkRow(i, 1)} disabled={i === bulkRows.length - 1} className="mono-label link-underline disabled:opacity-30 disabled:no-underline" data-testid={`bulk-down-${i}`}>↓ Move down</button>
                  <button type="button" onClick={() => removeBulkRow(i)} disabled={bulkRows.length <= 1} className="mono-label link-underline text-destructive disabled:opacity-30 disabled:no-underline ml-auto" data-testid={`bulk-remove-${i}`}>× Remove</button>
                </div>
              </div>
              );
            })}
          </div>
          <div className="mt-6 flex items-center justify-between">
            <button onClick={() => setBulkRows((rs) => [...rs, EMPTY_ROW()])} className="mono-label link-underline" data-testid="bulk-add-row">+ Add another row</button>
            <button onClick={submitBulk} disabled={createBulk.isPending} className="mono-label px-6 py-3 bg-marine text-paper hover:opacity-90 transition-opacity disabled:opacity-40" data-testid="bulk-submit">
              {createBulk.isPending ? "Creating…" : `Create ${bulkRows.filter(r => r.title.trim()).length} projects →`}
            </button>
          </div>
        </div>
      )}

      {isLoading && <div className="mono-label py-24">Loading…</div>}

      {/* ---------- Existing projects table ---------- */}
      <div className="flex items-baseline justify-between mb-4 mt-8 flex-wrap gap-3" data-testid="admin-home-summary">
        <div className="mono-label">
          Projects ({projects.length}) — <span className="text-marine">{projects.filter(p => p.featured).length} on Home</span>
        </div>
        <div className="flex items-center gap-6 flex-wrap">
          <button
            onClick={() => {
              if (window.confirm(`Renumber all ${projects.length} projects to 0…${projects.length - 1} in the current display order?`)) {
                renumberAll.mutate();
              }
            }}
            disabled={renumberAll.isPending || projects.length === 0}
            className="mono-label link-underline text-marine disabled:opacity-40 disabled:no-underline"
            title="Reset every project's priority to match its current display order — cleans up sparse or duplicate values."
            data-testid="admin-renumber-all"
          >
            {renumberAll.isPending ? "Renumbering…" : "↺ Renumber 0…N"}
          </button>
          <div className="mono-label text-ash hidden md:block">
            Toggle ★ to add / remove from the Home page. Type a position (1 = first) and press Enter — other projects shift down automatically.
          </div>
        </div>
      </div>
      <table className="w-full text-left" data-testid="admin-projects-table">
        <thead className="border-b border-ink/15">
          <tr>
            <th className="mono-label py-3 pr-4">#</th>
            <th className="mono-label py-3 pr-4">Title</th>
            <th className="mono-label py-3 pr-4">Discipline</th>
            <th className="mono-label py-3 pr-4">Year</th>
            <th className="mono-label py-3 pr-4">Status</th>
            <th className="mono-label py-3 pr-4 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((p, i) => {
            const hero = p.gallery?.find((g) => g.id === p.hero_image_id) || p.gallery?.[0];
            return (
              <tr key={p.id} className="border-b border-ink/15" data-testid={`admin-row-${i}`}>
                <td className="py-6 pr-4 align-top">
                  {p.hero_media_type === "video" && p.hero_video_path ? (
                    <div className="w-16 h-16 bg-ink flex items-center justify-center mono-label text-paper">VID</div>
                  ) : hero ? (
                    <img src={fileUrl(hero.variants.thumb)} alt="" className="w-16 h-16 object-cover" />
                  ) : (
                    <div className="w-16 h-16 bg-bone" />
                  )}
                </td>
                <td className="py-6 pr-4 align-top">
                  <Link to={`/admin/projects/${p.id}`} className="font-display italic text-2xl link-underline" data-testid={`admin-edit-${i}`}>
                    {p.title}
                  </Link>
                  <div className="mono-label text-ash mt-1">/{p.slug}</div>
                </td>
                <td className="py-6 pr-4 align-top mono-label">{p.discipline}</td>
                <td className="py-6 pr-4 align-top mono-label">{p.year}</td>
                <td className="py-6 pr-4 align-top">
                  <button
                    onClick={() => togglePublish.mutate({ id: p.id, published: !p.published })}
                    className={`mono-label block ${p.published ? "text-marine" : "text-ash"}`}
                    data-testid={`admin-publish-${i}`}
                  >
                    {p.published ? "● Published" : "○ Draft"}
                  </button>
                  <button
                    onClick={() => toggleFeatured.mutate({ id: p.id, featured: !p.featured })}
                    className={`mono-label block mt-1 ${p.featured ? "text-marine" : "text-ash"}`}
                    title={p.featured ? "Currently shown on Home — click to remove" : "Click to add to Home page"}
                    data-testid={`admin-featured-${i}`}
                  >
                    {p.featured ? "★ On Home" : "☆ Add to Home"}
                  </button>
                </td>
                <td className="py-6 pr-4 align-top text-right">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-end gap-2 md:gap-4">
                    <div className="flex items-center gap-1 border border-ink/15 md:mr-2" data-testid={`admin-reorder-${i}`}>
                      <button
                        type="button"
                        onClick={() => moveProject(i, -1)}
                        disabled={i === 0 || swapOrder.isPending}
                        className="mono-label px-3 py-1 hover:bg-bone disabled:opacity-30 disabled:hover:bg-transparent"
                        aria-label={`Move ${p.title} up`}
                        title="Move up — appears earlier on Work"
                        data-testid={`admin-move-up-${i}`}
                      >↑</button>
                      <input
                        type="number"
                        min={1}
                        max={projects.length}
                        step={1}
                        defaultValue={i + 1}
                        key={`order-${p.id}-${i}`}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.currentTarget.blur(); }
                          if (e.key === "Escape") {
                            e.currentTarget.value = String(i + 1);
                            e.currentTarget.blur();
                          }
                        }}
                        onBlur={(e) => {
                          const raw = e.target.value.trim();
                          if (raw === "") { e.target.value = String(i + 1); return; }
                          const typed = parseInt(raw, 10);
                          if (Number.isNaN(typed)) { e.target.value = String(i + 1); return; }
                          // 1-based UI → 0-based index. Clamp to list length.
                          const targetIndex = Math.max(0, Math.min(projects.length - 1, typed - 1));
                          if (targetIndex === i) { e.target.value = String(i + 1); return; }
                          placeAt.mutate({ id: p.id, position: targetIndex });
                        }}
                        className="mono-label text-ash w-12 text-center bg-transparent border-x border-ink/15 py-1 focus:outline-none focus:text-marine focus:bg-bone/40 tabular-nums no-spinner"
                        aria-label={`Position of ${p.title} — type a number to move it`}
                        title={`Position ${i + 1} of ${projects.length}. Type a number (1 = first) and press Enter.`}
                        data-testid={`admin-order-input-${i}`}
                      />
                      <button
                        type="button"
                        onClick={() => moveProject(i, 1)}
                        disabled={i === projects.length - 1 || swapOrder.isPending}
                        className="mono-label px-3 py-1 hover:bg-bone disabled:opacity-30 disabled:hover:bg-transparent"
                        aria-label={`Move ${p.title} down`}
                        title="Move down — appears later on Work"
                        data-testid={`admin-move-down-${i}`}
                      >↓</button>
                    </div>
                    <Link to={`/admin/projects/${p.id}`} className="mono-label link-underline">Edit</Link>
                    <button
                      onClick={() => { if (window.confirm(`Delete ${p.title}?`)) deleteMut.mutate(p.id); }}
                      className="mono-label text-ash hover:text-marine"
                      data-testid={`admin-delete-${i}`}
                    >Delete</button>
                  </div>
                </td>
              </tr>
            );
          })}
          {!isLoading && projects.length === 0 && (
            <tr><td colSpan={6} className="py-16 text-center mono-label">No projects yet. Create the first.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
